/*eslint complexity: ["error", 24]*/
import {
  CreateUpdateEstimateItemDto,
  CreateUpdateEstimateItemDtoPositionTypeEnum,
  GetPositionExtendedDtoPositionTypeEnum,
  GetProfileDto,
  ProfileSchemaDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { PositionTypeEnum } from '../../../common/pokCore/validation/enums';
import {
  AmountFieldType,
  EstimateItemFieldsDefinition,
} from '../../../common/pokCore/validation/estimateItemFields.definition';

export const fieldsToClear = (
  currentPositionType: GetPositionExtendedDtoPositionTypeEnum,
  oldPositionType: GetPositionExtendedDtoPositionTypeEnum | undefined,
  profile?: GetProfileDto,
) => {
  const profileSupportsCommission = profile?.commission || false;
  const profileSupportsTechnicalCost = profile?.technicalCost || false;
  if (currentPositionType === oldPositionType) {
    return {};
  }
  if (!profileSupportsCommission && !profileSupportsTechnicalCost) {
    return {};
  }

  if (
    !profileSupportsCommission &&
    (currentPositionType ===
      GetPositionExtendedDtoPositionTypeEnum.Commission ||
      currentPositionType ===
        GetPositionExtendedDtoPositionTypeEnum.TransferredCommission)
  ) {
    throw new Error(
      `Profile does not support position type ${currentPositionType}`,
    );
  }
  if (
    !profileSupportsTechnicalCost &&
    currentPositionType === GetPositionExtendedDtoPositionTypeEnum.TechnicalCost
  ) {
    throw new Error(
      `Profile does not support position type ${currentPositionType}`,
    );
  }

  const result: Record<string, null | boolean> = {};

  const amountFieldsToNull = [
    ...EstimateItemFieldsDefinition.amountFieldsKeys,
    ...EstimateItemFieldsDefinition.percentageFieldsKeys,
  ];

  amountFieldsToNull.forEach(field => {
    result[field.name] = null;
  });

  const parameterFields = [
    ...EstimateItemFieldsDefinition.stringFields,
    ...EstimateItemFieldsDefinition.boolFields,
  ];
  // Filtruj pola w zależności od typu pozycji
  const parameterFieldsToNull = parameterFields.filter(field => {
    if (
      currentPositionType === GetPositionExtendedDtoPositionTypeEnum.Standard
    ) {
      return !field.standard;
    }
    if (
      currentPositionType === GetPositionExtendedDtoPositionTypeEnum.Commission
    ) {
      return !field.commission;
    }
    if (
      currentPositionType ===
      GetPositionExtendedDtoPositionTypeEnum.TransferredCommission
    ) {
      return !field.transferredCommission;
    }
    return false;
  });

  parameterFieldsToNull.forEach(field => {
    result[field.name] = field.name === 'isUpSell' ? false : null;
  });

  // Dodaj pola słownikowe z sufiksem 'Id'
  EstimateItemFieldsDefinition.dictionaryFields
    .filter(field => {
      if (currentPositionType === PositionTypeEnum.Standard) {
        return !field.standard;
      }
      if (currentPositionType === PositionTypeEnum.Commission) {
        return !field.commission;
      }
      if (currentPositionType === PositionTypeEnum.TransferredCommission) {
        return !field.transferredCommission;
      }
      return false;
    })
    .forEach(field => {
      result[`${field.name}Id`] = null;
    });

  //nie nullujemy pola countryDictionaryId i ballDictionaryId
  delete result['countryDictionaryId'];
  delete result['ballDictionaryId'];

  return result;
};

export const isSalesTotalDisabled = (
  schema: ProfileSchemaDto,
  item: CreateUpdateEstimateItemDto,
  profile?: GetProfileDto,
): boolean => {
  const salesFields = [
    ...EstimateItemFieldsDefinition.amountFieldsKeys.filter(
      f => f.type === AmountFieldType.sales && f.name !== 'salesNetTotal',
    ),
    ...EstimateItemFieldsDefinition.percentageFieldsKeys.filter(
      f => f.type === AmountFieldType.sales,
    ),
  ];

  return salesFields.some(
    field =>
      schema[field.name as keyof ProfileSchemaDto] &&
      !isFieldHidden(field.name, item.positionType, profile),
  );
};

export const isPurchaseTotalDisabled = (
  schema: ProfileSchemaDto,
  item: CreateUpdateEstimateItemDto,
  profile?: GetProfileDto,
): boolean => {
  const purchaseFields = [
    ...EstimateItemFieldsDefinition.amountFieldsKeys.filter(
      f => f.type === AmountFieldType.purchase && f.name !== 'purchaseNetTotal',
    ),
    ...EstimateItemFieldsDefinition.percentageFieldsKeys.filter(
      f => f.type === AmountFieldType.purchase,
    ),
  ];

  return purchaseFields.some(
    field =>
      schema[field.name as keyof ProfileSchemaDto] &&
      !isFieldHidden(field.name, item.positionType, profile),
  );
};

export const isFieldHidden = (
  fieldnameToHide: string,
  positionType: CreateUpdateEstimateItemDtoPositionTypeEnum,
  profile?: GetProfileDto,
): boolean => {
  const profileSupportsCommissions = profile?.commission || false;
  const profileSupportsTechnicalCost = profile?.technicalCost || false;
  if (!profileSupportsCommissions && !profileSupportsTechnicalCost) {
    return false;
  }

  const allFields = [
    ...EstimateItemFieldsDefinition.amountFieldsKeys,
    ...EstimateItemFieldsDefinition.percentageFieldsKeys,
    ...EstimateItemFieldsDefinition.stringFields,
    ...EstimateItemFieldsDefinition.boolFields,
    ...EstimateItemFieldsDefinition.dictionaryFields,
  ];

  const field = allFields.find(f => f.name === fieldnameToHide);
  if (!field) {
    throw new Error(`Field ${fieldnameToHide} not found in definition`);
  }

  switch (positionType) {
    case PositionTypeEnum.Standard:
      return profileSupportsCommissions ? !field.standard : false;
    case PositionTypeEnum.Commission:
      return !field.commission;
    case PositionTypeEnum.TransferredCommission:
      return !field.transferredCommission;
    case PositionTypeEnum.TechnicalCost:
      return !field.technicalCost;
    default:
      return !field.standard;
  }
};
